import { Button, LoadingIcon } from '@kalos/ui';
import { useEffect, useRef } from 'react';

import { ROWS_PER_PAGE } from '../constants';

type LoadMoreSentinelProps = {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** True when loading the next page, or refreshing the loaded ones, failed. */
  isError: boolean;
  onLoadMore: () => void;
  /** What Retry does after an error. Defaults to `onLoadMore`. */
  onRetry?: () => void;
  /** How many records are on screen, for the end-of-list line. */
  loadedCount: number;
  /** Plural noun for the records, for example "documents". */
  itemLabel: string;
};

/**
 * Sits after the last record of a scrolling list and loads the next page when it scrolls
 * into view. Replaces page buttons on phones.
 */
export const LoadMoreSentinel = ({
  hasNextPage,
  isFetchingNextPage,
  isError,
  onLoadMore,
  onRetry = onLoadMore,
  loadedCount,
  itemLabel,
}: LoadMoreSentinelProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const canLoadMore = hasNextPage && !isFetchingNextPage && !isError;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !canLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
      },
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMore, onLoadMore]);

  // Nothing to say (the list is short, or the next page isn't due yet): no padding, so a short
  // list doesn't end in an empty band.
  const hasMessage = isError || isFetchingNextPage || (!hasNextPage && loadedCount > ROWS_PER_PAGE);

  return (
    <div
      ref={sentinelRef}
      className={
        hasMessage
          ? 'text-muted-foreground flex flex-col items-center gap-2 px-4 py-4 text-sm'
          : 'h-px'
      }
    >
      {isError ? (
        <>
          <p role="alert" className="text-danger-foreground">
            Couldn&apos;t load more {itemLabel}.
          </p>
          <Button variant="outline" onClick={onRetry}>
            Retry loading more {itemLabel}
          </Button>
        </>
      ) : isFetchingNextPage ? (
        <p role="status" className="flex items-center gap-2">
          <LoadingIcon />
          Loading more {itemLabel}…
        </p>
      ) : (
        // Only once more than one page has loaded; a short list needs no "all shown".
        !hasNextPage &&
        loadedCount > ROWS_PER_PAGE && (
          <p className="tabular-nums">
            All {loadedCount} {itemLabel} shown
          </p>
        )
      )}
    </div>
  );
};
